import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ReactComponent as BlueCheck } from '../../../assets/images/svg/blue-check.svg';
import { ReactComponent as RedCross } from '../../../assets/images/svg/red-cross.svg';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const CompletedIcon = styled(BlueCheck)`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  z-index: 1;
`;

const CancelledIcon = styled(RedCross)`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  z-index: 1;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div`
  display: flex;
  gap: 12px;
  position: relative;
  padding-bottom: 24px;

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 12px;
    transform: translateX(-50%);
    top: 24px;
    bottom: 0;
    width: 1px;
    background: ${themeColors.colorBorderPrimary};
  }
`;

const Dot = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  background: ${themeColors.colorWhite};
  border: 1px solid ${themeColors.colorBorderPrimary};
`;

const PendingDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${themeColors.colorTextSecondary};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Label = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${({ $active }) =>
    $active ? themeColors.colorPrimary : themeColors.colorTextSecondary};
`;

const SubText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorTextSecondary};
`;

const CancelLink = styled.button`
  align-self: flex-start;
  margin-top: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorError};
  text-decoration: underline;
  text-decoration-skip-ink: auto;
`;

const SellFromHomeStatusTimeline = ({ steps, cancelLabel, onCancelOrder }) => (
  <List data-cy="sfh-status-timeline">
    {steps.map((step) => {
      const active = Boolean(step.completed || step.cancelled);
      return (
        <Item key={step.key}>
          {step.completed && !step.cancelled && <CompletedIcon />}
          {step.cancelled && <CancelledIcon />}
          {!step.completed && !step.cancelled && (
            <Dot>
              <PendingDot />
            </Dot>
          )}
          <Content>
            <Label $active={active}>{step.label}</Label>
            {step.subText && <SubText>{step.subText}</SubText>}
            {step.showCancel && typeof onCancelOrder === 'function' && (
              <CancelLink
                type="button"
                onClick={onCancelOrder}
                data-cy="link-sfh-cancel-order"
              >
                {cancelLabel}
              </CancelLink>
            )}
            {step.notice}
          </Content>
        </Item>
      );
    })}
  </List>
);

SellFromHomeStatusTimeline.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      completed: PropTypes.bool,
      cancelled: PropTypes.bool,
      subText: PropTypes.string,
      showCancel: PropTypes.bool,
      notice: PropTypes.node,
    }),
  ),
  cancelLabel: PropTypes.string,
  onCancelOrder: PropTypes.func,
};

export default SellFromHomeStatusTimeline;
